//!visibility is available in edit only not in add
import { useEffect, useMemo, useReducer } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import useAxios from "../../../../MainComponents/Hooks/useAxios";
import HelmetTags from "../../../../MainComponents/HelmetTags";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import Loader from "../../../../MainComponents/Loader";
import dayjs from "dayjs";
import { DatePicker } from "antd";

function FieldError({ children }) {
  if (!children) return null;
  return <p className="admin-course-form__error">{children}</p>;
}

function AddEditCourse({ edit }) {
  const { courseID } = useParams();
  function reducer(state, action) {
    switch (action.type) {
      case "setSubmitAddEditCourse": {
        return { ...state, submitAddEditCourse: action.payload };
      }
      default:
        throw Error("Unknown action: " + action.type);
    }
  }

  const [state, dispatch] = useReducer(reducer, {
    submitAddEditCourse: { flag: "", dependency: false, data: {} },
  });

  const api = edit
    ? process.env.REACT_APP_ADMIN_EDIT_COURSE_API
    : process.env.REACT_APP_ADMIN_ADD_COURSE_API;

  const [CourseAddEditSuccess, CourseAddEditErrors, submitLoading] = useAxios(
    api,
    "POST",
    state.submitAddEditCourse.flag,
    state.submitAddEditCourse.dependency,
    state.submitAddEditCourse.data,
    true
  );

  const [CourseInfo, , CourseInfoLoading] = useAxios(
    `${process.env.REACT_APP_ADMIN_COURSE_EDIT_API}${courseID}`,
    "GET",
    courseID,
    courseID
  );

  const [NextCourses] = useAxios(
    process.env.REACT_APP_ADMIN_NEXT_COURSES_API,
    "GET",
    "GET",
    ""
  );

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    reset,
    control,
  } = useForm({
    mode: "onTouched",
    defaultValues: {},
    reValidateMode: "onChange",
    shouldUnregister: false,
  });

  const watchsequence = watch("sequence");
  const watchStage = watch("stage");
  const watchSection = watch("section");
  const watchSystemType = watch("system_type");
  const serverErrors = CourseAddEditErrors?.response?.data?.errors;

  const onSubmit = (data) => {
    const finalData = courseID
      ? {
          ...data,
          id: courseID,
          img: data?.img[0]?.name ? data?.img[0] : null,
          description: data?.description || null,
          visibility: +data?.visibility,
          is_buying: +data?.is_buying,
          next_course: +data.sequence === 0 ? null : data.next_course,
          section: +data.stage === 3 ? data.section : null,
          system_type: +data.stage === 3 ? data.system_type : "normal",
          expired_at: data.expired_at
            ? dayjs(data.expired_at).format("YYYY-MM-DD HH:mm:ss")
            : null,
          available_at: data.available_at
            ? dayjs(data.available_at).format("YYYY-MM-DD HH:mm:ss")
            : null,
          hold_at: data.hold_at
            ? dayjs(data.hold_at).format("YYYY-MM-DD HH:mm:ss")
            : null,
        }
      : {
          ...data,
          img: data?.img[0]?.name ? data?.img[0] : null,
          sequence: data?.sequence ? data?.sequence : 0,
          description: data.description || null,
          next_course: +data.sequence === 0 ? null : data.next_course,
          section: +data.stage === 3 ? data.section : null,
          system_type: +data.stage === 3 ? data.system_type : "normal",
          expired_at: data.expired_at
            ? dayjs(data.expired_at).format("YYYY-MM-DD HH:mm:ss")
            : null,
          available_at: data.available_at
            ? dayjs(data.available_at).format("YYYY-MM-DD HH:mm:ss")
            : null,
          hold_at: data.hold_at
            ? dayjs(data.hold_at).format("YYYY-MM-DD HH:mm:ss")
            : null,
        };

    dispatch({
      type: "setSubmitAddEditCourse",
      payload: {
        flag: "AddEditCourse",
        dependency: !state.submitAddEditCourse.dependency,
        data: finalData,
      },
    });
  };

  const filteredCourses = useMemo(() => {
    if (!NextCourses?.data?.length) return [];

    let filtered = NextCourses.data.filter(
      (course) => course.stage === +watchStage
    );

    if (+watchStage === 3) {
      filtered = filtered.filter((course) => course.section === watchSection);
    }

    if (+watchStage === 3 && watchSystemType) {
      filtered = filtered.filter((course) => {
        if (watchSystemType === "all") return true;
        return (
          course.system_type === watchSystemType ||
          course.system_type === "all"
        );
      });
    }

    if (courseID) {
      filtered = filtered.filter((course) => course.key !== +courseID);
    }

    return filtered;
  }, [watchStage, watchSection, watchSystemType, NextCourses, courseID]);

  useEffect(() => {
    if (CourseInfo && NextCourses?.data?.length > 0) {
      const temp = { ...CourseInfo.data };
      delete temp.img;

      reset({
        ...temp,
        expired_at: temp.expired_at
          ? dayjs(temp.expired_at).format("YYYY-MM-DD HH:mm:ss")
          : "",
        available_at: temp.available_at
          ? dayjs(temp.available_at).format("YYYY-MM-DD HH:mm:ss")
          : "",
        hold_at: temp.hold_at
          ? dayjs(temp.hold_at).format("YYYY-MM-DD HH:mm:ss")
          : "",
        visibility: temp.visibility === 1,
        is_buying: temp.is_buying === 1,
        section: temp.section,
        system_type: temp.system_type || "normal",
        sequence: String(temp.sequence ?? 0),
        batch: temp?.batch,
        next_course: temp?.next_course,
      });
    }
  }, [CourseInfo, NextCourses, reset]);

  useEffect(() => {
    if (CourseAddEditSuccess) {
      navigate(-1);
      reset();
    }
  }, [CourseAddEditSuccess, navigate, reset]);

  if (CourseInfoLoading) return <Loader />;

  return (
    <section className="admin-course-form-page dark:bg-dark">
      <HelmetTags title="Courses | Mr.Mohamed Salama" />

      <div className="form-container admin-course-form-wrap">
        <h2 className="admin-course-form-title">
          {courseID ? "Edit Course" : "Add Course"}
        </h2>

        <form
          onSubmit={handleSubmit(onSubmit)}
          encType="multipart/form-data"
          method="post"
          className="admin-course-form rounded-3xl bg-white"
        >
          <div className="admin-course-form__section">
            <h3 className="admin-course-form__section-title">
              1. Basic Info
            </h3>

            <div className="admin-course-form__field">
              <label htmlFor="title">Name</label>
              <input
                className="signin-inputs"
                type="text"
                id="title"
                placeholder="Course name"
                autoComplete="on"
                {...register("title", {
                  required: true,
                  maxLength: 80,
                  minLength: 3,
                })}
              />
              <FieldError>
                {errors.title?.type === "required" && "This field is required."}
                {errors.title?.type === "maxLength" &&
                  "Allowed Name max length is 80."}
                {errors.title?.type === "minLength" &&
                  "Please enter at least 3 letters."}
                {serverErrors?.title?.[0]}
              </FieldError>
            </div>

            <div className="admin-course-form__grid">
              <div className="admin-course-form__field">
                <label htmlFor="branch">Branch</label>
                <select
                  id="branch"
                  {...register("branch", { required: true })}
                >
                  <option value="statistics">Statistics</option>
                  <option value="dynamics">Dynamics</option>
                  <option value="calculus">Calculus</option>
                  <option value="algebra">Algebra</option>
                  <option value="statics">Statics</option>
                  <option value="applied">Applied</option>
                  <option value="pure">Pure</option>
                </select>
                <FieldError>
                  {errors.branch?.type === "required" &&
                    "This field is required."}
                  {serverErrors?.branch?.[0]}
                </FieldError>
              </div>

              <div className="admin-course-form__field">
                <label htmlFor="stage">Stage</label>
                <select id="stage" {...register("stage", { required: true })}>
                  <option value="1">1st sec</option>
                  <option value="2">2nd sec</option>
                  <option value="3">3rd sec</option>
                </select>
                <FieldError>
                  {errors.stage?.type === "required" &&
                    "This field is required."}
                  {serverErrors?.stage?.[0]}
                </FieldError>
              </div>
            </div>

            {+watchStage === 3 && (
              <div className="admin-course-form__field">
                <label>Section</label>
                <div className="admin-course-form__radio-row">
                  <label className="admin-course-form__radio" htmlFor="scientific">
                    <input
                      type="radio"
                      id="scientific"
                      value="scientific"
                      {...register("section", { required: true })}
                    />
                    <span>Scientific</span>
                  </label>
                  <label className="admin-course-form__radio" htmlFor="literary">
                    <input
                      type="radio"
                      id="literary"
                      value="literary"
                      {...register("section", { required: true })}
                    />
                    <span>Literary</span>
                  </label>
                </div>
                <FieldError>
                  {errors.section?.type === "required" &&
                    "This field is required."}
                  {serverErrors?.section?.[0]}
                </FieldError>
              </div>
            )}

            {+watchStage === 3 && (
              <div className="admin-course-form__field">
                <label>System</label>
                <div className="admin-course-form__radio-row">
                  <label
                    className="admin-course-form__radio"
                    htmlFor="course_all_system"
                  >
                    <input
                      type="radio"
                      id="course_all_system"
                      value="all"
                      {...register("system_type", { required: true })}
                    />
                    <span>للكل</span>
                  </label>
                  <label
                    className="admin-course-form__radio"
                    htmlFor="course_hand_by_hand"
                  >
                    <input
                      type="radio"
                      id="course_hand_by_hand"
                      value="hand_by_hand"
                      {...register("system_type", { required: true })}
                    />
                    <span>ايد بايد</span>
                  </label>
                  <label
                    className="admin-course-form__radio"
                    htmlFor="course_normal_system"
                  >
                    <input
                      type="radio"
                      id="course_normal_system"
                      value="normal"
                      {...register("system_type", { required: true })}
                    />
                    <span>النظام العادي</span>
                  </label>
                </div>
                <FieldError>
                  {errors.system_type?.type === "required" &&
                    "This field is required."}
                  {serverErrors?.system_type?.[0]}
                </FieldError>
              </div>
            )}
          </div>

          <div className="admin-course-form__section">
            <h3 className="admin-course-form__section-title">
              2. Sequence & Schedule
            </h3>

            <div className="admin-course-form__grid">
              <div className="admin-course-form__field">
                <label>Sequence</label>
                <div className="admin-course-form__radio-row">
                  <label className="admin-course-form__radio" htmlFor="yes">
                    <input
                      type="radio"
                      id="yes"
                      value={1}
                      checked={+watchsequence === 1}
                      {...register("sequence", { required: true })}
                    />
                    <span>Yes</span>
                  </label>
                  <label className="admin-course-form__radio" htmlFor="no">
                    <input
                      type="radio"
                      id="no"
                      value={0}
                      checked={+watchsequence === 0}
                      {...register("sequence", { required: true })}
                    />
                    <span>No</span>
                  </label>
                </div>
                <FieldError>
                  {errors.sequence?.type === "required" &&
                    "This field is required."}
                  {serverErrors?.sequence?.[0]}
                </FieldError>
              </div>

              {+watchsequence === 1 && (
                <div className="admin-course-form__field">
                  <label htmlFor="next_course">Required Course</label>
                  <select
                    id="next_course"
                    {...register("next_course", {
                      required: true,
                      valueAsNumber: true,
                      min: 1,
                    })}
                  >
                    {filteredCourses?.map((course) => (
                      <option value={course.key} key={course.key}>
                        {course.title}
                      </option>
                    ))}
                  </select>
                  <FieldError>
                    {errors.next_course?.type === "required" &&
                      "This field is required."}
                    {serverErrors?.next_course?.[0]}
                  </FieldError>
                </div>
              )}
            </div>

            <div className="admin-course-form__grid admin-course-form__grid--3">
              <div className="admin-course-form__field">
                <label htmlFor="available_at">Available At</label>
                <Controller
                  control={control}
                  name="available_at"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <DatePicker
                      id="available_at"
                      className="signin-inputs w-full admin-course-form__picker"
                      showTime
                      format="YYYY-MM-DD HH:mm:ss"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date) =>
                        field.onChange(date?.toISOString() || null)
                      }
                      placeholder="Select date and time"
                    />
                  )}
                />
                <FieldError>
                  {errors.available_at?.type === "required" &&
                    "This field is required."}
                  {serverErrors?.available_at?.[0]}
                </FieldError>
              </div>

              <div className="admin-course-form__field">
                <label htmlFor="expired_at">Expired At</label>
                <Controller
                  control={control}
                  name="expired_at"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <DatePicker
                      id="expired_at"
                      className="signin-inputs w-full admin-course-form__picker"
                      showTime
                      format="YYYY-MM-DD HH:mm:ss"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date) =>
                        field.onChange(date?.toISOString() || null)
                      }
                      placeholder="Select date and time"
                    />
                  )}
                />
                <FieldError>
                  {errors.expired_at?.type === "required" &&
                    "This field is required."}
                  {serverErrors?.expired_at?.[0]}
                </FieldError>
              </div>

              <div className="admin-course-form__field">
                <label htmlFor="hold_at">Hold At</label>
                <Controller
                  control={control}
                  name="hold_at"
                  render={({ field }) => (
                    <DatePicker
                      id="hold_at"
                      className="signin-inputs w-full admin-course-form__picker"
                      showTime
                      format="YYYY-MM-DD HH:mm:ss"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date) =>
                        field.onChange(date?.toISOString() || null)
                      }
                      placeholder="Select date and time"
                    />
                  )}
                />
                <FieldError>{serverErrors?.hold_at?.[0]}</FieldError>
              </div>
            </div>
          </div>

          <div className="admin-course-form__section">
            <h3 className="admin-course-form__section-title">
              3. Pricing & Media
            </h3>

            {edit && (
              <div className="admin-course-form__grid">
                <div className="admin-course-form__toggle">
                  <label className="visibility-switch">
                    <input
                      id="visibility"
                      type="checkbox"
                      {...register("visibility", { required: false })}
                    />
                    <span className="visibility-slider">
                      <svg
                        className="slider-icon"
                        viewBox="0 0 32 32"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path fill="none" d="m4 16.5 8 8 16-16" />
                      </svg>
                    </span>
                  </label>
                  <label htmlFor="visibility">Visibility</label>
                </div>

                <div className="admin-course-form__toggle">
                  <label className="visibility-switch">
                    <input
                      id="is_buying"
                      type="checkbox"
                      {...register("is_buying", { required: false })}
                    />
                    <span className="visibility-slider">
                      <svg
                        className="slider-icon"
                        viewBox="0 0 32 32"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path fill="none" d="m4 16.5 8 8 16-16" />
                      </svg>
                    </span>
                  </label>
                  <label htmlFor="is_buying">Available for purchase</label>
                </div>
              </div>
            )}

            <div className="admin-course-form__grid">
              <div className="admin-course-form__field">
                <label htmlFor="price">Price</label>
                <input
                  className="signin-inputs"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  id="price"
                  placeholder="000"
                  {...register("price", {
                    required: true,
                    valueAsNumber: true,
                    min: 0,
                  })}
                />
                <FieldError>
                  {errors.price?.type === "required" &&
                    "This field is required."}
                  {errors.price?.type === "min" && "Minimum Number is 0."}
                  {serverErrors?.price?.[0]}
                </FieldError>
              </div>

              <div className="admin-course-form__field">
                <label htmlFor="batch">Batch</label>
                <select id="batch" {...register("batch", { required: true })}>
                  <option value="" disabled>
                    Select Batch
                  </option>
                  <option value="2024-2025">2024-2025</option>
                  <option value="2025-2026">2025-2026</option>
                  <option value="2026-2027">2026-2027</option>
                </select>
                <FieldError>
                  {errors.batch?.type === "required" &&
                    "This field is required."}
                  {serverErrors?.batch?.[0]}
                </FieldError>
              </div>
            </div>

            <div className="admin-course-form__field">
              <label htmlFor="img">Course Image</label>
              <input
                id="img"
                className="signin-inputs"
                type="file"
                accept="image/jpg, image/jpeg, image/png, image/webp"
                {...register("img", {
                  required: edit ? false : true,
                  validate: (value) => !(value?.[0]?.size > 5000000),
                })}
              />
              <FieldError>
                {errors.img?.type === "required" && "This field is required."}
                {errors.img?.type === "validate" && "Image Max Size is 5 MB."}
                {serverErrors?.img?.[0]}
              </FieldError>
            </div>

            <div className="admin-course-form__field">
              <label htmlFor="description">Description</label>
              <textarea
                className="admin-course-form__textarea"
                id="description"
                rows="5"
                placeholder="Optional short description"
                {...register("description", { maxLength: 155, minLength: 3 })}
              />
              <FieldError>
                {errors.description?.type === "maxLength" &&
                  "Allowed Description Max Length is 155."}
                {errors.description?.type === "minLength" &&
                  "Please Enter at Least 3 Letters."}
                {serverErrors?.description?.[0]}
              </FieldError>
            </div>
          </div>

          <button
            disabled={
              !isValid || (submitLoading && state.submitAddEditCourse.flag)
            }
            className="submit admin-course-form__submit"
            type="submit"
          >
            {submitLoading && state.submitAddEditCourse.flag ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : courseID ? (
              "Confirm Editing The Course"
            ) : (
              "Add The Course"
            )}
          </button>
        </form>
      </div>
    </section>
  );
}

export default AddEditCourse;
